import {DateDimension, Dimension, FunnelAttributes, HavingFilterOperator, TopFilterInput, TopFilterOrder} from '../../../../globalTypes';
import {AppSplit, KnownMeasure, TimeBucket} from 'app/features/analytics/features/reports/dimension';
import {ReportsLayoutType} from 'app/features/analytics/features/reports/components/reports-page/reports-layout.enum';
import {SortDirection, Theme} from 'app/core/interfaces/common.interface';
import {ReportsMode} from 'app/features/analytics/shared/components/analytics-table/analytics-table.helpers';
import {ColorSettings} from 'app/features/analytics/shared/components/analytics-table/components/form-coloring-cells/form-coloring-cells.component';
import {isAnyTimeDimension} from 'app/features/analytics/shared/utils';

export enum FilterMode {
    Include,
    Exclude,
    Contains,
    DoesNotContains,
}

export interface FilterValue {
    id: Dimension | string;
    value?: string | string[];
    label?: string;
    include?: boolean;
    mode?: FilterMode;
    search?: string;
}

export interface TimeFilterValue extends FilterValue {
    id: DateDimension.DATE | DateDimension.INSTALL_DATE;
    from: string;
    to: string;
    relativeDate?: string | null;
}

export interface SelectedMeasure {
    id: string;
    day: number | null;
    label: string;
    having?: Record<HavingFilterOperator, number>;
}

export interface MeasureToAdd {
    id: string | KnownMeasure;
    day?: number | null;
    having?: Record<HavingFilterOperator, string | number>;
}

export interface Group {
    id: string;
}

export interface AppGroup extends Group {
    id: string;
    display: AppSplit;
}

export interface NetworkAccountGroup extends Group {
    id: string;
    display: AppSplit;
}

export interface TimeGroup extends Group {
    id: string;
    timeBucket: TimeBucket;
}

export interface ColumnSortBy {
    field: string;
    order: SortDirection;
}

export interface ColumnPinned {
    field: string;
    pinned: boolean | string | null;
}

export interface Pagination {
    page: number;
    pageSize: number;
}

export interface ReportFilters {
    filter: FilterValue[];
    groupBy: Group[];
    campaignProps?: Group[];
    userId?: string;
    measures: SelectedMeasure[];
    sortBy?: ColumnSortBy[];
    isDemo?: boolean;
    forecastVersion?: string;
    isMultiMediation?: boolean;
    view?: ReportsLayoutType;
    mode?: ReportsMode;
    columnPinned?: ColumnPinned[];
    topFilter?: TopFilterInput;
    order?: Record<string, number>;
    colorFormatting?: Record<KnownMeasure, ColorSettings>;
    chartWithMetrics?: Record<KnownMeasure, SelectedMeasure[]>;
    funnelFilter?: FunnelAttributes;
    topLimit?: number;
    topByMetric?: SelectedMeasure;
    savedReport?: boolean;
    pagination?: Pagination;
    savedReportId?: string;
}

export const DEFAULT_TOP_LIMIT = 7;

export function dimensionAvailableFilterModes(dm: Dimension | string): FilterMode[] {
    return (dm as string).includes('ATTRIBUTION_') ||
        [
            Dimension.KEYWORDS,
            Dimension.PACKAGE_VERSION,
            Dimension.INSTALL_PACKAGE_VERSION,
            Dimension.SDK_VERSION,
            Dimension.MANUFACTURER,
            Dimension.MODEL,
            Dimension.PLUGIN_VERSION,
            Dimension.OS_VERSION,
            Dimension.FRAMEWORK_VERSION,
            Dimension.FRAMEWORK,
            Dimension.DAY_SINCE_INSTALL,
            Dimension.NETWORK_ACCOUNT,
            Dimension.EVENT_NAME,
            Dimension.STUDIO,
        ].includes(dm as Dimension)
        ? [FilterMode.Include, FilterMode.Exclude, FilterMode.Contains, FilterMode.DoesNotContains]
        : [FilterMode.Include, FilterMode.Exclude];
}

export function getChartTopFilter<T extends ReportFilters>(filters: T, theme: Theme, includeOthers): T {
    const groupBy = filters.groupBy.find(x => x?.id && !isAnyTimeDimension(x.id));
    if (!groupBy) {
        return filters;
    }
    filters.topFilter = {
        dimension: groupBy.id as Dimension,
        includeOthers,
        limit: theme.chartsDefaults.topLimit,
        measure: {
            day: null,
            id: KnownMeasure.AdRevenue,
        },
        order: TopFilterOrder.TopValues,
    };
    if (filters.topFilter.dimension === Dimension.AD_NETWORK) {
        filters.topFilter.limit = 30;
    }
    return filters;
}
